import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { SignupSchema } from "@travel/shared";
import { api, ApiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/common/Button";

export function SignupPage() {
  const { user, isLoading, refresh } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const signupMutation = useMutation({
    mutationFn: api.auth.signup,
    onSuccess: async () => {
      await refresh();
      navigate("/", { replace: true });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : "회원가입 실패");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-slate-500">
        불러오는 중…
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const result = SignupSchema.safeParse({ email, name, password });
    if (!result.success) {
      setError(result.error.issues.map((i) => i.message).join("\n"));
      return;
    }
    signupMutation.mutate(result.data);
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">회원가입</h1>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-sm text-slate-600 mb-1 block">이메일</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-600 mb-1 block">이름</span>
            <input
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={40}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-600 mb-1 block">비밀번호 (8자 이상)</span>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
            />
          </label>
          {error && (
            <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
              {error}
            </pre>
          )}
          <Button type="submit" className="w-full" disabled={signupMutation.isPending}>
            {signupMutation.isPending ? "가입 중…" : "회원가입"}
          </Button>
        </form>
        <p className="text-sm text-slate-500 text-center">
          이미 계정이 있으신가요?{" "}
          <Link to="/login" className="text-slate-900 underline">
            로그인
          </Link>
        </p>
      </div>
    </div>
  );
}
